<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <b-img
            :src="require('@/assets/images/logo/logo.png')"
            alt="logo"
            style="max-width: 40px"
          />
          <h2 class="brand-text text-primary ml-1">
            MES - IT Login
          </h2>
        </b-link>

        <b-card-title class="mb-1">
          Login Khusus IT 🔐
        </b-card-title>
        <b-card-text class="mb-2">
          Silakan masukkan NIK dan password untuk akses IT
        </b-card-text>

        <!-- Alert for errors -->
        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
          dismissible
          @dismissed="errorMessage = ''"
        >
          <div class="alert-body">
            {{ errorMessage }}
          </div>
        </b-alert>

        <!-- form -->
        <validation-observer
          ref="loginForm"
          #default="{ invalid }"
        >
          <b-form
            class="auth-login-form mt-2"
            @submit.prevent="login"
          >
            <!-- NIK -->
            <b-form-group
              label-for="nik"
              label="NIK"
            >
              <validation-provider
                #default="{ errors }"
                name="NIK"
                rules="required"
              >
                <b-form-input
                  id="nik"
                  v-model="nik"
                  name="login-nik"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Masukkan NIK"
                  autofocus
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- password -->
            <b-form-group>
              <label for="password">Password</label>
              <validation-provider
                #default="{ errors }"
                name="Password"
                rules="required"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="password"
                    v-model="password"
                    :type="passwordFieldType"
                    class="form-control-merge"
                    :state="errors.length > 0 ? false : null"
                    name="login-password"
                    placeholder="Masukkan Password"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- loading spinner -->
            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="invalid || isLoading"
            >
              <b-spinner
                v-if="isLoading"
                small
                class="mr-1"
              />
              {{ isLoading ? 'Memproses...' : 'Login' }}
            </b-button>
          </b-form>
        </validation-observer>

        <b-card-text class="text-center mt-2">
          <b-link :to="{ name: 'auth-login' }">
            <small>← Kembali ke Login Utama</small>
          </b-link>
        </b-card-text>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardTitle,
  BCardText,
  BInputGroup,
  BInputGroupAppend,
  BAlert,
  BImg,
  BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import useJwt from '@/auth/jwt/useJwt'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

export default {
  components: {
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BCardTitle,
    BLink,
    BCardText,
    BInputGroup,
    BInputGroupAppend,
    BAlert,
    BImg,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      nik: '',
      password: '',
      errorMessage: '',
      isLoading: false,
      required,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.isLoading = true
          this.errorMessage = ''

          let apiBase = process.env.VUE_APP_AXIOS_URL || '/'
          // Ensure it ends with slash
          if (!apiBase.endsWith('/')) {
            apiBase += '/'
          }

          // Step 1: Authenticate IT user with NIK + password (pakai API existing)
          axios({
            method: 'post',
            url: `${apiBase}api_mes/user-mes/view`,
            params: {
              nik: this.nik,
              pass: this.password,
            },
          })
            .then(response => {
              if (!response.data.data || response.data.data.length === 0) {
                this.errorMessage = 'NIK atau Password salah!'
                this.isLoading = false
                return
              }

              const userData = response.data.data[0]

              // Validasi: user harus punya nama
              if (!userData.nama || userData.nama === '') {
                this.errorMessage = 'Anda tidak memiliki akses ke aplikasi MES!'
                this.isLoading = false
                return
              }

              // Step 2: Get hak akses
              axios({
                method: 'post',
                url: `${apiBase}api_mes/hak-akses/view`,
                params: {
                  emp_id: userData.id,
                },
              })
                .then(response2 => {
                  // Set localStorage using encrypted session
                  import('@/utils/session').then(({ setEncryptedSession }) => {
                    const sessionData = {
                      menu: response2.data.data,
                      id: userData.id,
                      nik: userData.nik,
                      nama: userData.nama,
                      jabatan: userData.jabatan,
                      email: userData.email,
                      grade: userData.nm_grade,
                      // Note: token is no longer needed in localStorage, but if some legacy code needs it,
                      // it's better not to store it. The backend handles it via cookie.
                      // accessToken: response.data.token
                    };
                    
                    setEncryptedSession(sessionData);
                    window.localStorage.setItem('isLoggedIn', 'true');
                    window.localStorage.setItem('accessToken', 'dummy_token_to_bypass_jwtService'); // Temporary if legacy needs it
                    
                    // Step 3: Login to JWT (same as main Login.vue)
                    useJwt.login({
                      email: 'admin@demo.com',
                      password: 'admin',
                    })
                      .then(jwtResponse => {
                        const jwtUserData = jwtResponse.data.userData
                        useJwt.setToken(jwtResponse.data.accessToken)
                        useJwt.setRefreshToken(jwtResponse.data.refreshToken)
                        localStorage.setItem('userData', JSON.stringify(jwtUserData))
                        this.$ability.update(jwtUserData.ability)
  
                        this.$store.commit(
                          'app-ecommerce/UPDATE_CART_ITEMS_COUNT',
                          jwtUserData.extras.eCommerceCartItemsCount,
                        )
  
                        // Force reload agar layout dan menu terbaca dari localStorage
                        window.location.replace('/')
                      })
                      .catch(() => {
                        this.errorMessage = 'Gagal autentikasi sistem. Hubungi administrator.'
                        this.isLoading = false
                      })
                  });
                })
                .catch(() => {
                  this.errorMessage = 'Gagal mengambil hak akses. Hubungi administrator.'
                  this.isLoading = false
                })
            })
            .catch(error => {
              if (error.response && error.response.status === 401) {
                this.errorMessage = 'NIK atau Password salah!'
              } else if (error.response && error.response.status === 403) {
                this.errorMessage = 'Anda tidak memiliki akses!'
              } else {
                this.errorMessage = 'Gagal terhubung ke server. Coba lagi nanti.'
              }
              this.isLoading = false
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
